/**
 * 인사관리 > 직원관리 페이지
 *
 * 전체 직원 목록을 조회하고 관리하는 페이지입니다.
 * - Firestore `users` 컬렉션에서 실시간 데이터 조회
 * - 검색/필터 기능 (이름/이메일/사번, 조직, 재직상태)
 * - 직원 목록 테이블
 * - 행 클릭 시 상세 페이지로 이동
 *
 * **권한:**
 * - hr.manage 권한 필요 (hr, super_admin만 접근 가능)
 *
 * **데이터 소스:**
 * - Firestore `users` 컬렉션
 * - useEmployees Hook 사용
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertCircle, Loader, UserPlus, Upload, Trash2, X, CheckCircle, Download, Filter as FilterIcon } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import * as XLSX from 'xlsx';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useOrgUnitsPublic } from '../../shared/hooks/useOrgUnitsPublic';
import { useOrgUnitSubtreeFilter } from '../../shared/hooks/useOrgUnitSubtreeFilter';
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { usePositionLevels } from '../../shared/hooks/usePositionLevels';
import { useEmploymentTypes } from '../../shared/hooks/useEmploymentTypes';
import {
  useEmployeeDeleteMutations,
  DeleteEmployeesResult,
} from '../../shared/hooks/useEmployeeDeleteMutations';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import {
  EMPLOYEE_STATUS,
  EMPLOYEE_STATUS_LABELS,
  EMPLOYEE_STATUS_COLORS,
} from '../../shared/constants/employeeStatus';
import { HrEmployeeFormModal } from './components/HrEmployeeFormModal';
import { HrEmployeeExcelUploadModal } from './components/HrEmployeeExcelUploadModal';
import { ModalOverlay } from '../../shared/components/ModalOverlay';

/**
 * 색상 이름을 CSS 변수로 매핑
 * (Phase 3-3: Employee Status UI 적용)
 * (V43: 퇴사 상태 가독성 개선 - gray → red)
 */
const COLOR_TO_CSS_VAR: Record<string, string> = {
  green: 'var(--color-success)',
  orange: 'var(--color-warning)',
  gray: '#dc2626', // V43: 퇴사(terminated) 빨간색으로 변경 (기존: var(--color-gray-400))
};

/**
 * HrEmployeesPage 컴포넌트
 */
export function HrEmployeesPage() {
  const navigate = useNavigate();

  // 검색/필터 상태
  const [searchText, setSearchText] = useState('');
  const [filterOrgUnit, setFilterOrgUnit] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // V29.15: 국가(회사) 필터 상태
  const [filterCompanyCode, setFilterCompanyCode] = useState<'KR' | 'CN' | 'ALL'>('ALL');

  // 추가 필터 상태
  const [filterNationalityType, setFilterNationalityType] = useState<string>('all');
  const [filterEducationLevel, setFilterEducationLevel] = useState<string>('all');
  const [filterEmploymentType, setFilterEmploymentType] = useState<string>('all');
  const [filterPosition, setFilterPosition] = useState<string>('all');
  const [filterJobTitle, setFilterJobTitle] = useState<string>('all');

  // 상세 필터 접기/펼치기 상태
  const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);

  // 모달 상태
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);

  // 직원 삭제 관련 상태
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<string>>(new Set());
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteEmployeesResult | null>(null);

  // V36: 엑셀 다운로드 관련 상태 (항상 원문 포함)
  const [isExporting, setIsExporting] = useState(false);
  const authContext = useAuthContext();

  // 삭제 Hook
  const { deleteEmployees, isDeleting } = useEmployeeDeleteMutations();

  // V60: role_exec 읽기 전용 - hr.manage 없으면 쓰기 버튼 숨김
  const { hasPermission } = useAuthorization();
  const canManageHr = hasPermission('hr.manage');

  // V29.15: 글로벌 스코프 변경 권한 확인
  // system_admin 또는 role_global 역할이 있으면 국가 필터 UI 노출
  const hasGlobalScopeAccess = useMemo(() => {
    // roleAssignments에서 roleId 추출
    const roleIds = authContext?.roleAssignments?.map((ra) => ra.roleId) || [];
    return roleIds.includes('system_admin') || roleIds.includes('role_global');
  }, [authContext?.roleAssignments]);

  // V73.2: 조직 subtree 필터 (공통 훅)
  const { orgUnits: allOrgUnits } = useOrgUnitsPublic({ isActive: true });
  const { descendantOrgUnitIds, orgDropdownOptions: rawOrgDropdownOptions } = useOrgUnitSubtreeFilter(allOrgUnits, filterOrgUnit);

  // V314: 조직 필터 드롭다운을 회사 스코프에 맞게 제한
  // - system_admin/role_global: filterCompanyCode 따름 (ALL → 전체, KR/CN → 해당 국가만)
  // - role_admin_kr/role_hr_kr 등 KR 역할: KR 조직만
  // - role_admin_cn/role_hr_cn 등 CN 역할: CN 조직만
  // - 기타: 본인 orgUnit의 companyCode 기반
  const orgDropdownOptions = useMemo(() => {
    if (!rawOrgDropdownOptions.length) return rawOrgDropdownOptions;

    // 글로벌 스코프 사용자: 국가 필터 선택값 따름
    if (hasGlobalScopeAccess) {
      if (filterCompanyCode === 'ALL') return rawOrgDropdownOptions;
      return rawOrgDropdownOptions.filter(ou => (ou as any).companyCode === filterCompanyCode);
    }

    // 비글로벌 사용자: 역할에서 국가 추론
    const roleIds = authContext?.roleAssignments?.map((ra) => ra.roleId) || [];
    const hasKrRole = roleIds.some(r => r.endsWith('_kr'));
    const hasCnRole = roleIds.some(r => r.endsWith('_cn'));

    if (hasKrRole && !hasCnRole) {
      return rawOrgDropdownOptions.filter(ou => (ou as any).companyCode === 'KR');
    }
    if (hasCnRole && !hasKrRole) {
      return rawOrgDropdownOptions.filter(ou => (ou as any).companyCode === 'CN');
    }

    // 역할로 판단 불가 시: 본인 orgUnit의 companyCode 기반
    if (authContext?.orgUnitId) {
      const myOrgUnit = allOrgUnits.find(ou => ou.id === authContext.orgUnitId);
      if (myOrgUnit?.companyCode) {
        return rawOrgDropdownOptions.filter(ou => (ou as any).companyCode === myOrgUnit.companyCode);
      }
    }

    return rawOrgDropdownOptions;
  }, [rawOrgDropdownOptions, hasGlobalScopeAccess, filterCompanyCode, authContext?.roleAssignments, authContext?.orgUnitId, allOrgUnits]);

  /**
   * Firestore에서 직원 목록 조회
   * V29.15: requestedCompanyCode 파라미터 추가
   * V73: 조직 필터 시 하위조직 포함 (orgUnitIds[] 사용)
   */
  const { employees, isLoading, error } = useEmployees({
    searchText,
    orgUnitIds: descendantOrgUnitIds,
    orgUnitId: descendantOrgUnitIds ? undefined : 'all',
    status: filterStatus,
    // V29.15: 글로벌 스코프 권한이 있을 때만 요청
    requestedCompanyCode: hasGlobalScopeAccess ? filterCompanyCode : undefined,
  });

  /**
   * 마스터데이터 미리 로드
   * - 직원 추가 모달에서 직위/직책 셀렉트가 즉시 표시되도록 함
   * - 페이지 마운트 시 Firestore 실시간 리스너가 설정됨
   */
  const { jobTitles } = useJobTitles({ status: 'active' });
  const { positionLevels } = usePositionLevels({ status: 'active' });
  const { employmentTypes } = useEmploymentTypes();

  // orgDropdownOptions: V73.2 공통 훅에서 제공

  /**
   * 최종학력 목록 추출 (중복 제거)
   */
  const educationLevels = useMemo(() => {
    const levels = employees
      .map(e => e.educationLevelLabel)
      .filter((level): level is string => !!level);
    return Array.from(new Set(levels));
  }, [employees]);

  /**
   * 고용형태 목록 추출 (중복 제거) - 실제 직원 데이터 기반
   */
  const employmentTypeOptions = useMemo(() => {
    const types = employees
      .map(e => e.currentEmploymentType)
      .filter((type): type is string => !!type);
    return Array.from(new Set(types));
  }, [employees]);

  /**
   * 필터링된 직원 목록
   * V77: 퇴사자 기본 제외 (퇴사자 관리 메뉴로 분리)
   */
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      // V77: 퇴사자 제외 (퇴사자 관리 메뉴에서 별도 관리)
      if (employee.status === EMPLOYEE_STATUS.TERMINATED) {
        return false;
      }

      // 내/외국인 필터
      if (filterNationalityType !== 'all' && employee.nationalityType !== filterNationalityType) {
        return false;
      }

      // 최종학력 필터
      if (filterEducationLevel !== 'all' && employee.educationLevelLabel !== filterEducationLevel) {
        return false;
      }

      // 고용형태 필터
      if (filterEmploymentType !== 'all' && employee.currentEmploymentType !== filterEmploymentType) {
        return false;
      }

      // 직위 필터
      if (filterPosition !== 'all' && employee.positionTitle !== filterPosition) {
        return false;
      }

      // 직책 필터
      if (filterJobTitle !== 'all' && employee.jobTitle !== filterJobTitle) {
        return false;
      }

      return true;
    });
  }, [employees, filterNationalityType, filterEducationLevel, filterEmploymentType, filterPosition, filterJobTitle]);

  /**
   * 필터 초기화 핸들러
   */
  const handleResetFilters = () => {
    setFilterNationalityType('all');
    setFilterEducationLevel('all');
    setFilterEmploymentType('all');
    setFilterPosition('all');
    setFilterJobTitle('all');
  };

  /**
   * 행 클릭 핸들러 - 상세 페이지로 이동
   */
  const handleRowClick = (employeeId: string) => {
    navigate(`/hr/employees/${employeeId}`, { state: { from: '/hr/employees' } });
  };

  /**
   * 체크박스 선택 핸들러
   */
  const handleSelectEmployee = (employeeId: string, checked: boolean) => {
    setSelectedEmployeeIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(employeeId);
      } else {
        next.delete(employeeId);
      }
      return next;
    });
  };

  /**
   * 전체 선택/해제 핸들러
   */
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEmployeeIds(new Set(filteredEmployees.map(e => e.id)));
    } else {
      setSelectedEmployeeIds(new Set());
    }
  };

  /**
   * 삭제 확인 다이얼로그 열기
   */
  const handleOpenDeleteConfirm = () => {
    if (selectedEmployeeIds.size === 0) {
      alert('삭제할 직원을 선택해주세요.');
      return;
    }
    setIsDeleteConfirmOpen(true);
    setDeleteResult(null);
  };

  /**
   * 삭제 실행
   */
  const handleConfirmDelete = async () => {
    try {
      const result = await deleteEmployees(Array.from(selectedEmployeeIds));
      setDeleteResult(result);

      // 성공한 경우 선택 해제 및 새로고침
      if (result.deletedCount > 0) {
        setSelectedEmployeeIds(new Set());
      }
    } catch (err: any) {
      alert(err.message || '직원 삭제에 실패했습니다.');
    }
  };

  /**
   * 삭제 다이얼로그 닫기
   */
  const handleCloseDeleteDialog = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteResult(null);
    // 삭제 성공 시 페이지 새로고침
    if (deleteResult && deleteResult.deletedCount > 0) {
      window.location.reload();
    }
  };

  /**
   * V36: 선택한 직원 엑셀 다운로드 (항상 원문 포함)
   */
  const handleExportExcel = async () => {
    if (selectedEmployeeIds.size === 0) {
      alert('내보낼 직원을 선택해주세요.');
      return;
    }

    setIsExporting(true);

    try {
      const functions = getFunctions();
      const exportFn = httpsCallable<
        { employeeIds: string[] },
        { success: boolean; data: any[]; filename: string; totalCount: number; includedSensitiveRaw: boolean }
      >(functions, 'exportEmployees');

      // V36: 항상 원문 포함 (includeSensitiveRaw 파라미터 제거)
      const payload = {
        employeeIds: Array.from(selectedEmployeeIds),
      };
      const result = await exportFn(payload);

      if (!result.data.success || !result.data.data.length) {
        throw new Error('내보낼 데이터가 없습니다.');
      }

      // V36: 민감정보 항상 원문으로 포함 (복호화 불필요)

      // 엑셀 헤더 매핑 (한글 컬럼명)
      // V40.2: 직급→직위 변경, certificatesText/trainingsText/commentText 추가
      // V32: 고용이력 기반 날짜 컬럼 추가
      const headerMap: Record<string, string> = {
        employeeNumber: '사번',
        nameKo: '이름',
        email: '이메일',
        phoneNumber: '전화번호',
        gender: '성별',
        birthDate: '생년월일',
        nationalityType: '내/외국인',
        residentRegistrationNo: '주민등록번호',
        address: '주소',
        educationLevel: '최종학력',
        major: '전공',
        // V40.2: 학력/기타 텍스트 필드 추가
        certificatesText: '자격증',
        trainingsText: '교육이수',
        commentText: '비고',
        affiliationName: '소속',
        orgUnitName: '부서',
        positionTitle: '직위',
        jobTitle: '직책',
        status: '재직상태',
        currentEmploymentType: '고용형태',
        firstHireDate: '최초입사일',
        ngleFirstHireDate: 'NGLE입사일',
        currentEmploymentStartDate: '계약시작일', // V67
        // V33: hireDate(현재입사일) 컬럼 제거 - dispatchHireDate 등 V32 컬럼으로 대체됨
        probationEndDate: '수습만료일',
        // V32: 고용형태별 입사일/만료일
        dispatchHireDate: '파견입사일',
        dispatchEndDate: '파견만료일',
        internHireDate: '인턴입사일',
        internEndDate: '인턴만료일',
        contractHireDate: '계약입사일',
        contractEndDate: '계약만료일',
        regularHireDate: '정규입사일',
        // 기존 컬럼
        leaveDate: '퇴사일',
        lastPromotionDate: '최종진급일',
        currentPositionStartDate: '현직위시작일',
      };

      // 데이터를 한글 컬럼명으로 변환
      const exportData = result.data.data.map((row: any, index: number) => {
        const newRow: Record<string, string> = {};
        Object.keys(headerMap).forEach((key) => {
          newRow[headerMap[key]] = row[key] || '';
        });

        return newRow;
      });

      // xlsx 워크시트 생성
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // 컬럼 너비 설정
      const colWidths = Object.values(headerMap).map((header) => ({
        wch: Math.max(header.length * 2, 12),
      }));
      worksheet['!cols'] = colWidths;

      // 워크북 생성 및 시트 추가
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '직원목록');

      // V36: 파일 다운로드 (항상 원문 포함)
      const filename = result.data.filename || `직원목록_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;

      XLSX.writeFile(workbook, filename);

      alert(`${result.data.totalCount}명의 직원 정보를 내보냈습니다.`);
    } catch (err: any) {
      console.error('[V36] 엑셀 내보내기 실패:', err);
      if (err.code === 'functions/permission-denied') {
        alert('엑셀 내보내기 권한이 없습니다.');
      } else {
        alert(err.message || '엑셀 내보내기에 실패했습니다.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * 선택된 직원 이름 목록 (미리보기용)
   */
  const selectedEmployeeNames = useMemo(() => {
    return employees
      .filter(e => selectedEmployeeIds.has(e.id))
      .map(e => e.nameKo || e.email || e.id)
      .slice(0, 5);
  }, [employees, selectedEmployeeIds]);

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* 페이지 제목 및 액션 버튼 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          margin: 0
        }}>
          직원 관리
        </h1>

        <div style={{ display: 'flex', gap: '12px' }}>
          {/* V36: 엑셀 다운로드 버튼 (선택된 직원이 있을 때만 표시, 항상 원문 포함) */}
          {selectedEmployeeIds.size > 0 && (
            <button
              onClick={() => handleExportExcel()}
              disabled={isExporting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                background: 'var(--color-success)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                opacity: isExporting ? 0.7 : 1,
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => !isExporting && (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => !isExporting && (e.currentTarget.style.opacity = '1')}
            >
              {isExporting ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>내보내는 중...</span>
                </>
              ) : (
                <>
                  <Download size={18} />
                  <span>엑셀 다운로드 ({selectedEmployeeIds.size}명)</span>
                </>
              )}
            </button>
          )}

          {/* 삭제 버튼 (선택된 직원이 있을 때만 표시, V60: role_exec 숨김) */}
          {canManageHr && selectedEmployeeIds.size > 0 && (
            <button
              onClick={handleOpenDeleteConfirm}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                background: 'var(--color-error)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <Trash2 size={18} />
              <span>선택 삭제 ({selectedEmployeeIds.size}명)</span>
            </button>
          )}

          {/* V60: role_exec 읽기 전용 - 직원 추가/엑셀 업로드 숨김 */}
          {canManageHr && (
            <>
              <button
                onClick={() => setIsAddModalOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: 'var(--color-primary)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                <UserPlus size={18} />
                <span>직원 추가</span>
              </button>

              <button
                onClick={() => setIsExcelModalOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: 'white',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  color: 'var(--color-gray-700)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-gray-50)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
              >
                <Upload size={18} />
                <span>엑셀 업로드</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* 검색/필터 영역 */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px',
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {/* 텍스트 검색 */}
        <div style={{ flex: '1 1 300px', position: 'relative' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-gray-400)'
            }}
          />
          <input
            type="text"
            placeholder="이름, 이메일, 사번으로 검색..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </div>

        {/* 조직 필터 */}
        <div style={{ flex: '0 0 200px' }}>
          <select
            value={filterOrgUnit}
            onChange={(e) => setFilterOrgUnit(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="all">전체 조직</option>
            {orgDropdownOptions.map(ou => (
              <option key={ou.id} value={ou.id}>{'　'.repeat(ou.level || 0)}{ou.name}</option>
            ))}
          </select>
        </div>

        {/* V77: 재직상태 필터 - 퇴사자 미노출 (퇴사자 관리 메뉴 분리) */}
        <div style={{ flex: '0 0 150px' }}>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="all">전체 상태</option>
            <option value={EMPLOYEE_STATUS.ACTIVE}>{EMPLOYEE_STATUS_LABELS[EMPLOYEE_STATUS.ACTIVE]}</option>
            <option value={EMPLOYEE_STATUS.ON_LEAVE}>{EMPLOYEE_STATUS_LABELS[EMPLOYEE_STATUS.ON_LEAVE]}</option>
          </select>
        </div>

        {/* V29.15: 국가(회사) 필터 - system_admin/role_global만 노출 */}
        {hasGlobalScopeAccess && (
          <div style={{ flex: '0 0 130px' }}>
            <select
              value={filterCompanyCode}
              onChange={(e) => {
                setFilterCompanyCode(e.target.value as 'KR' | 'CN' | 'ALL');
                setFilterOrgUnit('all'); // V314: 국가 변경 시 조직 필터 초기화
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid var(--color-primary)',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'var(--color-primary-light, #e8f4ff)',
                fontWeight: '500',
              }}
            >
              <option value="ALL">🌐 전체 국가</option>
              <option value="KR">🇰🇷 KR</option>
              <option value="CN">🇨🇳 CN</option>
            </select>
          </div>
        )}
      </div>

      {/* 추가 필터 영역 */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px',
      }}>
        <button
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0',
            marginBottom: isFilterExpanded ? '16px' : '0',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <FilterIcon size={18} style={{ color: 'var(--color-primary)' }} />
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: 'var(--color-gray-800)', margin: 0 }}>
              상세 필터
            </h3>
          </div>
          <span style={{
            fontSize: '14px',
            color: 'var(--color-gray-600)',
            transition: 'transform 0.2s',
            transform: isFilterExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            ▼
          </span>
        </button>

        {isFilterExpanded && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
          }}>
            {/* 직위 필터 */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--color-gray-700)' }}>
                직위
              </label>
              <select
                value={filterPosition}
                onChange={(e) => setFilterPosition(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                }}
              >
                <option value="all">전체</option>
                {positionLevels.map(position => (
                  <option key={position.id} value={position.name}>{position.name}</option>
                ))}
              </select>
            </div>

            {/* 직책 필터 */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--color-gray-700)' }}>
                직책
              </label>
              <select
                value={filterJobTitle}
                onChange={(e) => setFilterJobTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                }}
              >
                <option value="all">전체</option>
                {jobTitles.map(title => (
                  <option key={title.id} value={title.name}>{title.name}</option>
                ))}
              </select>
            </div>

            {/* 고용형태 필터 */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--color-gray-700)' }}>
                고용형태
              </label>
              <select
                value={filterEmploymentType}
                onChange={(e) => setFilterEmploymentType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                }}
              >
                <option value="all">전체</option>
                {employmentTypeOptions.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* 최종학력 필터 */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--color-gray-700)' }}>
                최종학력
              </label>
              <select
                value={filterEducationLevel}
                onChange={(e) => setFilterEducationLevel(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                }}
              >
                <option value="all">전체</option>
                {educationLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            {/* 내/외국인 필터 */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--color-gray-700)' }}>
                내/외국인
              </label>
              <select
                value={filterNationalityType}
                onChange={(e) => setFilterNationalityType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                }}
              >
                <option value="all">전체</option>
                <option value="domestic">내국인</option>
                <option value="foreign">외국인</option>
              </select>
            </div>

            {/* 초기화 버튼 */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={handleResetFilters}
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  background: 'var(--color-gray-100)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  color: 'var(--color-gray-700)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-gray-200)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-gray-100)')}
              >
                필터 초기화
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 로딩 상태 */}
      {isLoading && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <Loader size={32} style={{
            color: 'var(--color-primary)',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '16px', color: 'var(--color-gray-600)' }}>
            직원 목록을 불러오는 중...
          </p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && !isLoading && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid var(--color-error)',
        }}>
          <AlertCircle size={32} style={{ color: 'var(--color-error)' }} />
          <h3 style={{ marginTop: '16px', color: 'var(--color-error)' }}>
            직원 목록을 불러오는 중 오류가 발생했습니다
          </h3>
          <p style={{ marginTop: '8px', color: 'var(--color-gray-600)' }}>
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            새로고침
          </button>
        </div>
      )}

      {/* 검색 결과 정보 */}
      {!isLoading && !error && (
        <div style={{
          marginBottom: '12px',
          color: 'var(--color-gray-600)',
          fontSize: '14px'
        }}>
          총 <strong style={{ color: 'var(--color-primary)' }}>{filteredEmployees.length}</strong>명의 직원
        </div>
      )}

      {/* 직원 목록 테이블 */}
      {!isLoading && !error && (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}>
            <thead>
              <tr style={{
                background: 'var(--color-gray-50)',
                borderBottom: '2px solid var(--color-gray-200)'
              }}>
                <th style={{ ...tableHeaderStyle, width: '40px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={filteredEmployees.length > 0 && selectedEmployeeIds.size === filteredEmployees.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                </th>
                <th style={tableHeaderStyle}>이름</th>
                <th style={tableHeaderStyle}>사번</th>
                <th style={tableHeaderStyle}>이메일</th>
                <th style={tableHeaderStyle}>소속</th>
                <th style={tableHeaderStyle}>부서</th>
                <th style={tableHeaderStyle}>직위</th>
                <th style={tableHeaderStyle}>직책</th>
                <th style={tableHeaderStyle}>고용형태</th>
                <th style={tableHeaderStyle}>상태</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: 'var(--color-gray-500)'
                    }}
                  >
                    검색 결과가 없습니다.
                  </td>
                </tr>
              ) : (
                filteredEmployees.map(employee => (
                <tr
                  key={employee.id}
                  style={{
                    borderBottom: '1px solid var(--color-gray-200)',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                    background: selectedEmployeeIds.has(employee.id) ? 'var(--color-primary-light, #e8f4ff)' : 'white',
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedEmployeeIds.has(employee.id)) {
                      e.currentTarget.style.background = 'var(--color-gray-50)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedEmployeeIds.has(employee.id)) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <td
                    style={{ ...tableCellStyle, textAlign: 'center', width: '40px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployeeIds.has(employee.id)}
                      onChange={(e) => handleSelectEmployee(employee.id, e.target.checked)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td
                    style={tableCellStyle}
                    onClick={() => handleRowClick(employee.id)}
                  >
                    <strong style={{ color: 'var(--color-gray-800)' }}>
                      {employee.nameKo}
                    </strong>
                  </td>
                  <td style={tableCellStyle}>
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: 'var(--color-gray-600)'
                    }}>
                      {employee.employeeNumber || '-'}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <span style={{ color: 'var(--color-gray-600)' }}>
                      {employee.email || '-'}
                    </span>
                  </td>
                  <td style={tableCellStyle}>{employee.affiliationName || '-'}</td>
                  <td style={tableCellStyle}>{employee.orgUnitName || '-'}</td>
                  <td style={tableCellStyle}>{employee.positionTitle || '-'}</td>
                  <td style={tableCellStyle}>
                    {employee.jobTitle || '-'}
                  </td>
                  <td style={tableCellStyle}>{employee.currentEmploymentType || '-'}</td>
                  <td style={tableCellStyle}>
                    {(() => {
                      const status = employee.status || EMPLOYEE_STATUS.ACTIVE;
                      const colorName = EMPLOYEE_STATUS_COLORS[status];
                      const cssColor = COLOR_TO_CSS_VAR[colorName];

                      // V43: 퇴사 상태는 더 강조된 스타일 적용
                      const isTerminated = status === EMPLOYEE_STATUS.TERMINATED;

                      return (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: isTerminated ? '5px 14px' : '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: isTerminated ? '600' : '500',
                          background: isTerminated ? cssColor : `${cssColor}15`,
                          color: isTerminated ? 'white' : cssColor,
                          border: isTerminated ? 'none' : `1px solid ${cssColor}30`,
                        }}>
                          {EMPLOYEE_STATUS_LABELS[status]}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 모달 컴포넌트 */}
      <HrEmployeeFormModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setIsAddModalOpen(false);
          window.location.reload();
        }}
      />

      <HrEmployeeExcelUploadModal
        isOpen={isExcelModalOpen}
        onClose={() => setIsExcelModalOpen(false)}
        onSuccess={() => {
          window.location.reload();
        }}
      />

      {/* 삭제 확인 다이얼로그 */}
      {isDeleteConfirmOpen && (
        <ModalOverlay style={deleteDialogOverlayStyle} onClose={handleCloseDeleteDialog}>
          <div style={deleteDialogStyle} onClick={(e) => e.stopPropagation()}>
            {/* 헤더 */}
            <div style={deleteDialogHeaderStyle}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {deleteResult ? '삭제 완료' : '직원 삭제 확인'}
              </h2>
              <button
                onClick={handleCloseDeleteDialog}
                disabled={isDeleting}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  color: 'var(--color-gray-600)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* 본문 */}
            <div style={deleteDialogBodyStyle}>
              {deleteResult ? (
                // 삭제 결과 표시
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}>
                    <CheckCircle size={24} color="var(--color-success)" />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      총 {deleteResult.requestedCount}명 중 {deleteResult.deletedCount}명 삭제 완료
                    </span>
                  </div>

                  {deleteResult.failures.length > 0 && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      background: 'var(--color-error-light, #fee)',
                      border: '1px solid var(--color-error)',
                      borderRadius: '6px',
                    }}>
                      <div style={{
                        fontWeight: '600',
                        color: 'var(--color-error)',
                        marginBottom: '8px',
                      }}>
                        실패 목록:
                      </div>
                      <div style={{ maxHeight: '150px', overflow: 'auto' }}>
                        {deleteResult.failures.map((f, idx) => (
                          <div key={idx} style={{
                            fontSize: '13px',
                            color: 'var(--color-gray-700)',
                            padding: '4px 0',
                          }}>
                            {f.userId}: {f.reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // 삭제 확인 메시지
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}>
                    <AlertCircle size={24} color="var(--color-error)" />
                    <span style={{ fontSize: '16px', color: 'var(--color-gray-700)' }}>
                      선택한 <strong>{selectedEmployeeIds.size}명</strong>의 직원을 삭제하시겠습니까?
                    </span>
                  </div>

                  <div style={{
                    padding: '12px',
                    background: 'var(--color-gray-50)',
                    borderRadius: '6px',
                    marginBottom: '16px',
                  }}>
                    <div style={{
                      fontWeight: '500',
                      marginBottom: '8px',
                      color: 'var(--color-gray-700)',
                    }}>
                      삭제 대상:
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>
                      {selectedEmployeeNames.join(', ')}
                      {selectedEmployeeIds.size > 5 && ` 외 ${selectedEmployeeIds.size - 5}명`}
                    </div>
                  </div>

                  <div style={{
                    padding: '12px',
                    background: 'var(--color-warning-light, #fff3cd)',
                    border: '1px solid var(--color-warning, #ffc107)',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: 'var(--color-gray-700)',
                  }}>
                    <strong>주의:</strong> 삭제 시 직원의 모든 관련 데이터(고용이력, 휴가, 역할 할당 등)가
                    함께 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
                  </div>
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div style={deleteDialogFooterStyle}>
              {deleteResult ? (
                <button
                  onClick={handleCloseDeleteDialog}
                  style={{
                    padding: '10px 24px',
                    background: 'var(--color-primary)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  닫기
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCloseDeleteDialog}
                    disabled={isDeleting}
                    style={{
                      padding: '10px 20px',
                      background: 'white',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '6px',
                      color: 'var(--color-gray-700)',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      opacity: isDeleting ? 0.5 : 1,
                    }}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                    style={{
                      padding: '10px 20px',
                      background: 'var(--color-error)',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      opacity: isDeleting ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {isDeleting ? (
                      <>
                        <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        삭제 중...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        삭제 확인
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

// Loader 애니메이션을 위한 스타일 추가
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(style);

/**
 * 테이블 헤더 스타일
 */
const tableHeaderStyle: React.CSSProperties = {
  padding: '14px 16px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: '600',
  color: 'var(--color-gray-700)',
  whiteSpace: 'nowrap',
};

/**
 * 테이블 셀 스타일
 */
const tableCellStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: 'var(--color-gray-700)',
  textAlign: 'center',
};

/**
 * 삭제 다이얼로그 오버레이 스타일
 */
const deleteDialogOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

/**
 * 삭제 다이얼로그 스타일
 */
const deleteDialogStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  width: '90%',
  maxWidth: '500px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};

/**
 * 삭제 다이얼로그 헤더 스타일
 */
const deleteDialogHeaderStyle: React.CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--color-gray-200)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

/**
 * 삭제 다이얼로그 본문 스타일
 */
const deleteDialogBodyStyle: React.CSSProperties = {
  padding: '24px',
};

/**
 * 삭제 다이얼로그 푸터 스타일
 */
const deleteDialogFooterStyle: React.CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid var(--color-gray-200)',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
};

/**
 * ============================================
 * ✅ Firestore 연동 완료 (Chapter 3-2)
 * ============================================
 *
 * **변경 사항:**
 * - Mock 데이터 제거
 * - useEmployees Hook 사용하여 Firestore `users` 컬렉션 조회
 * - 로딩/에러 상태 처리 추가
 * - 검색/필터가 Hook 파라미터로 전달되어 클라이언트 단에서 처리됨
 *
 * **데이터 흐름:**
 * 1. 사용자가 검색어/필터 입력
 * 2. useEmployees({ searchText, orgUnitId, status }) 호출
 * 3. Hook이 Firestore에서 전체 목록 조회
 * 4. 클라이언트 단에서 필터링
 * 5. UI 업데이트
 *
 * **성능 최적화 (향후):**
 * - 현재: 전체 목록을 가져와 클라이언트 필터링
 * - 향후: Firestore Query where 조건으로 서버 사이드 필터링
 * - 향후: 페이지네이션 추가 (100명 이상 시)
 */

/**
 * ============================================
 * TODO: RBAC 권한 체크 강화 (향후)
 * ============================================
 *
 * 현재는 Sidebar에서 메뉴 필터링만 적용되어 있습니다.
 * URL 직접 접근 시에도 차단하려면 App.tsx에 Route Guard 추가 필요합니다.
 *
 * **구현 방법:**
 *
 * App.tsx에서 ProtectedRoute 확장:
 *
 * ```tsx
 * <Route
 *   path="hr/employees"
 *   element={
 *     <ProtectedRoute requiredPermission="hr.manage">
 *       <HrEmployeesPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
